/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFocusEffect, useNavigationState } from '@react-navigation/native';

export type TabScreenRefreshHandler = () => void | Promise<void>;

type TabScreenRefreshContextValue = {
  handlers: Record<string, TabScreenRefreshHandler>;
  activeTabName: string | undefined;
  setActiveTabName: (name: string | undefined) => void;
  register: (routeName: string, handler: TabScreenRefreshHandler) => void;
  unregister: (routeName: string) => void;
};

const TabScreenRefreshContext = createContext<TabScreenRefreshContextValue | null>(null);

export function TabScreenRefreshProvider({ children }: { children: React.ReactNode }) {
  const [handlers, setHandlers] = useState<Record<string, TabScreenRefreshHandler>>({});
  const [activeTabName, setActiveTabName] = useState<string | undefined>(undefined);

  const register = useCallback((routeName: string, handler: TabScreenRefreshHandler) => {
    setHandlers((prev) => (prev[routeName] === handler ? prev : { ...prev, [routeName]: handler }));
  }, []);

  const unregister = useCallback((routeName: string) => {
    setHandlers((prev) => {
      if (!(routeName in prev)) return prev;
      const next = { ...prev };
      delete next[routeName];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ handlers, activeTabName, setActiveTabName, register, unregister }),
    [handlers, activeTabName, register, unregister],
  );

  return (
    <TabScreenRefreshContext.Provider value={value}>{children}</TabScreenRefreshContext.Provider>
  );
}

export function useTabScreenRefreshContext() {
  const ctx = useContext(TabScreenRefreshContext);
  if (!ctx) {
    throw new Error('useTabScreenRefreshContext must be used within TabScreenRefreshProvider');
  }
  return ctx;
}

export function useTabScreenRefreshContextOptional() {
  return useContext(TabScreenRefreshContext);
}

export function useTabScreenRefreshUiOptional() {
  const ctx = useContext(TabScreenRefreshContext);
  return ctx ? { activeTabName: ctx.activeTabName } : null;
}

/** Nombre de la ruta hoja enfocada (ShellStack → MainTabs → Home, etc.). */
export function useDeepestFocusedRouteName(): string | undefined {
  return useNavigationState((state) => {
    if (!state) return undefined;
    let current: typeof state | undefined = state;
    let name: string | undefined;
    while (current?.routes?.length) {
      const route = current.routes[current.index ?? 0];
      if (!route) break;
      name = route.name;
      current = route.state as typeof state | undefined;
    }
    return name;
  });
}

/**
 * Handler de actualizar del header: prioriza la ruta hoja enfocada,
 * luego el activeTabName reclamado por focus de pantalla.
 */
export function useResolvedTabRefreshHandler(): TabScreenRefreshHandler | undefined {
  const ctx = useTabScreenRefreshContextOptional();
  const focused = useDeepestFocusedRouteName();
  if (!ctx) return undefined;
  if (focused && ctx.handlers[focused]) return ctx.handlers[focused];
  if (ctx.activeTabName && ctx.handlers[ctx.activeTabName]) {
    return ctx.handlers[ctx.activeTabName];
  }
  return undefined;
}

function useStableRefreshHandler(onRefresh: TabScreenRefreshHandler | undefined) {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const stableRef = useRef<TabScreenRefreshHandler | null>(null);
  if (stableRef.current == null) {
    stableRef.current = () => onRefreshRef.current?.();
  }
  return stableRef.current;
}

export function useRegisterTabScreenRefresh(routeName: string, onRefresh: TabScreenRefreshHandler) {
  const ctx = useContext(TabScreenRefreshContext);
  const stableHandler = useStableRefreshHandler(onRefresh);
  const register = ctx?.register;
  const unregister = ctx?.unregister;
  const setActiveTabName = ctx?.setActiveTabName;

  useFocusEffect(
    useCallback(() => {
      setActiveTabName?.(routeName);
    }, [routeName, setActiveTabName]),
  );

  useEffect(() => {
    if (!register || !unregister || !stableHandler) return;
    register(routeName, stableHandler);
    return () => unregister(routeName);
  }, [routeName, register, unregister, stableHandler]);
}

export function useShellScreenHeaderRefresh(routeName: string, onRefresh?: TabScreenRefreshHandler) {
  const ctx = useContext(TabScreenRefreshContext);
  const stableHandler = useStableRefreshHandler(onRefresh);
  const register = ctx?.register;
  const unregister = ctx?.unregister;
  const setActiveTabName = ctx?.setActiveTabName;
  const hasOnRefresh = onRefresh != null;

  useFocusEffect(
    useCallback(() => {
      setActiveTabName?.(routeName);
    }, [routeName, setActiveTabName]),
  );

  useEffect(() => {
    if (!hasOnRefresh || !register || !unregister || !stableHandler) return;
    register(routeName, stableHandler);
    return () => unregister(routeName);
  }, [routeName, hasOnRefresh, register, unregister, stableHandler]);
}
